import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  Button,
  Typography,
  Divider,
  Grid,
  Alert,
  Chip,
  IconButton,
  Collapse,
  Skeleton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Person, PersonFormData, Gender, LocationFormData } from '../../types';
import { ValidationService } from '../../services/validationService';
import { tokens } from '../../theme';

interface PersonFormProps {
  person?: Person;
  onSubmit: (data: PersonFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  title?: string;
}

const initialFormData: PersonFormData = {
  firstName: '',
  lastName: '',
  maidenName: '',
  nicknames: '',
  middleName: '',
  suffix: '',
  gender: 'male',
  birthDate: '',
  deathDate: '',
  isDeceased: false,
  birthPlace: { city: '', state: '', country: '' },
  deathPlace: { city: '', state: '', country: '' },
  currentLocation: { city: '', state: '', country: '' },
  occupation: '',
  education: '',
  biography: '',
  personalNotes: '',
  isPrivate: false,
};

export const PersonForm: React.FC<PersonFormProps> = ({
  person,
  onSubmit,
  onCancel,
  loading = false,
  title,
}) => {
  const [formData, setFormData] = useState<PersonFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    dates: true,
    locations: false,
    details: false,
    notes: false,
  });

  // Initialize form data when person prop changes
  useEffect(() => {
    if (person) {
      setFormData({
        firstName: person.firstName || '',
        lastName: person.lastName || '',
        maidenName: person.maidenName || '',
        nicknames: person.nicknames ? person.nicknames.join(', ') : '',
        middleName: person.middleName || '',
        suffix: person.suffix || '',
        gender: person.gender || 'male',
        birthDate: person.birthDate || '',
        deathDate: person.deathDate || '',
        isDeceased: person.isDeceased || false,
        birthPlace: person.birthPlace || { city: '', state: '', country: '' },
        deathPlace: person.deathPlace || { city: '', state: '', country: '' },
        currentLocation: person.currentLocation || { city: '', state: '', country: '' },
        occupation: person.occupation || '',
        education: person.education || '',
        biography: person.biography || '',
        personalNotes: person.personalNotes || '',
        isPrivate: person.isPrivate || false,
      });
    }
  }, [person]);

  const handleInputChange = (field: keyof PersonFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'checkbox'
      ? (event.target as HTMLInputElement).checked
      : event.target.value;
    
    setFormData((prev: PersonFormData) => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field as string]) {
      setErrors((prev: Record<string, string>) => ({ ...prev, [field]: '' }));
    }
  };

  const handleLocationChange = (location: keyof PersonFormData, field: keyof LocationFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev: PersonFormData) => ({
      ...prev,
      [location]: {
        ...(prev[location] as LocationFormData),
        [field]: event.target.value,
      },
    }));
  };

  const handleDateChange = (field: 'birthDate' | 'deathDate') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev: PersonFormData) => ({
      ...prev,
      [field]: event.target.value,
    }));
    
    // Clear error when date changes
    if (errors[field]) {
      setErrors((prev: Record<string, string>) => ({ ...prev, [field]: '' }));
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Validate form
    const validation = ValidationService.validatePersonForm(formData);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    setSubmitting(true);
    setErrors({});
    
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({
        submit: error instanceof Error ? error.message : 'An error occurred while saving',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getFormTitle = () => {
    if (title) return title;
    return person ? 'Edit Person' : 'Add New Person';
  };

  if (loading) {
    return (
      <Card>
        <CardHeader title={<Skeleton width="40%" />} />
        <CardContent>
          <Grid container spacing={3}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Skeleton variant="rectangular" height={56} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto' }}>
        <CardHeader
          title={
            <Typography variant="h5" component="h1" fontWeight={600}>
              {getFormTitle()}
            </Typography>
          }
          sx={{ pb: 1 }}
        />
        
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Error Alert */}
            {errors.submit && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errors.submit}
              </Alert>
            )}

            {/* Basic Information */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mb: 2,
                }}
                onClick={() => toggleSection('basic')}
              >
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
                  Basic Information
                </Typography>
                {expandedSections.basic ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              
              <Collapse in={expandedSections.basic}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange('firstName')}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                      disabled={submitting}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      required
                      value={formData.lastName}
                      onChange={handleInputChange('lastName')}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                      disabled={submitting}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Middle Name"
                      value={formData.middleName}
                      onChange={handleInputChange('middleName')}
                      error={!!errors.middleName}
                      helperText={errors.middleName}
                      disabled={submitting}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Maiden Name"
                      value={formData.maidenName}
                      onChange={handleInputChange('maidenName')}
                      error={!!errors.maidenName}
                      helperText={errors.maidenName}
                      disabled={submitting}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nicknames"
                      value={formData.nicknames}
                      onChange={handleInputChange('nicknames')}
                      error={!!errors.nicknames}
                      helperText={errors.nicknames || 'Separate multiple nicknames with commas'}
                      disabled={submitting}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Suffix"
                      value={formData.suffix}
                      onChange={handleInputChange('suffix')}
                      error={!!errors.suffix}
                      helperText={errors.suffix || 'e.g., Jr., Sr., III'}
                      disabled={submitting}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl component="fieldset" error={!!errors.gender}>
                      <FormLabel component="legend">Gender *</FormLabel>
                      <RadioGroup
                        row
                        value={formData.gender}
                        onChange={(e) => setFormData((prev: PersonFormData) => ({ ...prev, gender: e.target.value as Gender }))}
                      >
                        <FormControlLabel value="male" control={<Radio />} label="Male" disabled={submitting} />
                        <FormControlLabel value="female" control={<Radio />} label="Female" disabled={submitting} />
                        <FormControlLabel value="other" control={<Radio />} label="Other" disabled={submitting} />
                        <FormControlLabel value="unknown" control={<Radio />} label="Unknown" disabled={submitting} />
                      </RadioGroup>
                      {errors.gender && (
                        <Typography variant="caption" color="error">
                          {errors.gender}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </Collapse>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Dates */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mb: 2,
                }}
                onClick={() => toggleSection('dates')}
              >
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
                  Important Dates
                </Typography>
                {expandedSections.dates ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              
              <Collapse in={expandedSections.dates}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Birth Date"
                      type="date"
                      value={formData.birthDate}
                      onChange={handleDateChange('birthDate')}
                      error={!!errors.birthDate}
                      helperText={errors.birthDate}
                      disabled={submitting}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Death Date"
                      type="date"
                      value={formData.deathDate}
                      onChange={handleDateChange('deathDate')}
                      error={!!errors.deathDate}
                      helperText={errors.deathDate}
                      disabled={submitting || !formData.isDeceased}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.isDeceased}
                            onChange={handleInputChange('isDeceased')}
                            disabled={submitting}
                          />
                        }
                        label="Person is deceased"
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </Collapse>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Additional Details */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mb: 2,
                }}
                onClick={() => toggleSection('details')}
              >
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
                  Additional Details
                </Typography>
                {expandedSections.details ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
              
              <Collapse in={expandedSections.details}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Occupation"
                      value={formData.occupation}
                      onChange={handleInputChange('occupation')}
                      error={!!errors.occupation}
                      helperText={errors.occupation}
                      disabled={submitting}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Education"
                      value={formData.education}
                      onChange={handleInputChange('education')}
                      error={!!errors.education}
                      helperText={errors.education}
                      disabled={submitting}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Biography"
                      multiline
                      rows={4}
                      value={formData.biography}
                      onChange={handleInputChange('biography')}
                      error={!!errors.biography}
                      helperText={errors.biography || 'Brief life story and accomplishments'}
                      disabled={submitting}
                    />
                  </Grid>
                </Grid>
              </Collapse>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Privacy */}
            <Box sx={{ mb: 4 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isPrivate}
                      onChange={handleInputChange('isPrivate')}
                      disabled={submitting}
                    />
                  }
                  label="Mark as private (visible only to family members)"
                />
              </FormGroup>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={submitting}
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={<SaveIcon />}
                sx={{
                  minWidth: 120,
                }}
              >
                {submitting ? 'Saving...' : person ? 'Update' : 'Create'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
  );
};

export default PersonForm;